import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import {ColorNamedOption} from 'shells/select-option-components/color-named-option';
import type {ListOption} from 'shells/select-option-components/option';

import type {TagType} from 'nutshell-graphql-types';
import type {
    GetTagsBasic as GetTagsQuery,
    SelectCreateTag as SelectCreateTagMutation,
    SelectCreateTagVariables as SelectCreateTagMutationVariables,
} from 'nutshell-graphql-types';

import {GraphQLSelect} from './graphql-select';
import CREATE_TAG from './mutations/create-tag.graphql';
import GET_TAGS from './queries/tags.graphql';

type Props = {
    selectedId: string | null | undefined;
    options: ListOption[];
    tagType?: TagType;
    onChange: (newValue: ListOption) => any;
    refetch: () => Promise<any>;
    updateOptions: (newOption: ListOption) => void;
    onClearValue?: () => void;
    labelKey?: string;
    valueKey?: string;
    autoFocus?: boolean;
    autoBlur?: boolean;
    isLoading?: boolean;
    noResultsText?: string;
    styles?: any;
    menuPortalTarget?: HTMLElement;
    menuPlacement?: 'auto' | 'bottom' | 'top';
    defaultsToFirstItem?: boolean;
    // If true, you also need to provide a tagType
    isCreateable?: boolean;
    hasError?: boolean | null | undefined;
};

export const GraphQLSelectTagsOld = (props: Props) => {
    const [createTag] = useMutation<SelectCreateTagMutation, SelectCreateTagMutationVariables>(
        CREATE_TAG
    );
    const canCreateTag = Boolean(props.isCreateable && props.tagType);

    return (
        <GraphQLSelect
            {...props}
            onChange={(newValue: ListOption) => {
                if (
                    !props.options.find((option) => option.value === newValue.value) &&
                    canCreateTag
                ) {
                    const tempNewOption = {
                        value: newValue.value,
                        label: newValue.label,
                    } as const;
                    props.updateOptions(tempNewOption);

                    // This is a created value, need to run our mutation.
                    createTag({
                        variables: {
                            // @ts-expect-error
                            tag: {tag: {name: newValue.label, entityType: props.tagType}},
                        },
                        update: (proxy, mutationResults) => {
                            // Read the data from our cache for this query.
                            const cacheResult: GetTagsQuery | null | undefined = proxy.readQuery({
                                query: GET_TAGS,
                                variables: {
                                    first: 1000,
                                    input: {
                                        entityType: props.tagType,
                                    },
                                },
                            });

                            if (!cacheResult || !cacheResult.tags) {
                                return;
                            }

                            const newTag =
                                mutationResults &&
                                mutationResults.data &&
                                mutationResults.data.createTag &&
                                mutationResults.data.createTag.tag;

                            if (!newTag) return;

                            proxy.writeQuery({
                                query: GET_TAGS,
                                variables: {
                                    first: 1000,
                                    input: {
                                        entityType: props.tagType,
                                    },
                                },
                                data: {
                                    tags: {
                                        ...cacheResult.tags,
                                        edges: [
                                            {
                                                cursor: newTag.id,
                                                node: newTag,
                                                __typename: 'TagsEdge',
                                            },
                                            // @ts-expect-error
                                        ].concat(cacheResult.tags.edges),
                                    },
                                },
                            });

                            const newTagValue = {
                                value: newTag.id,
                                label: newTag.name,
                            } as const;

                            // Update our selected item to the new item
                            props.onChange(newTagValue);

                            // Update the tag options to include the new one immediately
                            props.updateOptions(newTagValue);

                            // Refetch the tag options from the server
                            props.refetch();
                        },
                    });
                } else {
                    return props.onChange(newValue);
                }
            }}
            isLoading={props.isLoading}
            // @ts-expect-error
            openMenuOnFocus={true}
            noResultsText='No tags were found'
            placeholder={canCreateTag ? 'Select or create a tag' : 'Select a tag'}
            valueKey='value'
            labelKey='label'
            components={{
                Option: ColorNamedOption,
            }}
            models={props.options}
            hasError={props.hasError}
        />
    );
};
